import React, { useState, useEffect } from "react";
import Navbar from "../Components/Navbar";
import { images } from "../assets/assets";
import {
  CircleCheck,
  ChevronDown,
  CalendarDays,
  Phone,
  MoveRight,
  Calendar,
  FileText,
  UserCheck,
} from "lucide-react";
import Footer from "../Components/Footer";
import { Link } from "react-router-dom";
import { getAllDoctors, createAppointment } from "../api";

const Appointment = () => {
 
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    facility: "",
    doctor: "",
    date: "",
    time: "",
    message: "",
    type: "appointment",
  });

  const [errors, setErrors] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const response = await getAllDoctors();
        const docArr = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.doctors)
          ? response.doctors
          : [];
        setDoctors(docArr);
      } catch (err) {
        console.error("Error fetching doctors:", err);
        setDoctors([]);
      } finally {
        setDoctorsLoading(false);
      }
    };

    fetchDoctors();
  }, []);

  const handleInputChange = (e) => {
    const { id, value } = e.target;

    // Special handling for doctor dropdown
    // if (id === "doctor") {
    //   const selectedDoctor = doctors.find((doc) => doc.name === value);
    //   setFormData((prev) => ({
    //     ...prev,
    //     doctor: selectedDoctor?._id || "",
    //   }));
    // } else {
    //   setFormData((prev) => ({
    //     ...prev,
    //     [id]: value,
    //   }));
    // }
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[id]) {
      setErrors((prev) => ({
        ...prev,
        [id]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Full Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (formData.fullName.trim().length < 3) {
      newErrors.fullName = "Name must be at least 3 characters";
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Please enter a valid 10-digit phone number";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Facility validation
    if (!formData.facility || formData.facility === "Select Facility") {
      newErrors.facility = "Please select a facility";
    }

    // Doctor validation
    if (!formData.doctor) {
      newErrors.doctor = "Please select a doctor";
    }

    // Date validation
    if (!formData.date) {
      newErrors.date = "Please select a preferred date";
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.date = "Please select a future date";
      }
    }

    // Time validation
    if (!formData.time) {
      newErrors.time = "Please select a time slot";
    }

    // Message validation (optional but if provided, min 10 chars)
    if (formData.message && formData.message.trim().length < 10) {
      newErrors.message = "Health concern must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!validateForm()) {
      console.log("❌ Form validation failed:", errors);
      return;
    }

    setSubmitting(true);
    try {
      // Prepare appointment data to send to API
      const appointmentData = {
        fullName: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        facility: formData.facility,
        doctor: formData.doctor,
        date: formData.date,
        time: formData.time,
        message: formData.message,
        type: formData.type,
      };

      console.log("✅ Form Data Submitted:", appointmentData);
      const response = await createAppointment(appointmentData);

      // Show success message from API response
      setSuccessMessage(
        response?.message || "Appointment booked successfully!",
      );
      console.log("Form submission successful!");

      // Reset form
      setFormData({
        fullName: "",
        phone: "",
        email: "",
        facility: "",
        doctor: "",
        date: "",
        time: "",
        message: "",
        type: "appointment",
      });
    } catch (error) {
      // Show error message
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to book appointment. Please try again.";
      setErrorMessage(errorMsg);
      console.error("❌ Error booking appointment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const getDoctorName = (doctorId) => {
    if (!doctorId) {
      return "";
    }
    const selectedDoctor = doctors.find((doc) => doc._id === doctorId);
    console.log("Selected Doctor", selectedDoctor);
    return selectedDoctor?.name || "";
  };

  // Success Modal
  const SuccessModal = () => (
    <>
      {successMessage && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-[20px] p-6 sm:p-8 max-w-[400px] w-full shadow-2xl animate-scale-in">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CircleCheck color="#22c55e" className="w-8 h-8" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                Success!
              </h2>
              <p className="text-gray-600 mb-6">{successMessage}</p>
              <button
                onClick={() => setSuccessMessage("")}
                className="w-full bg-[#1975FF] text-white py-2 sm:py-3 rounded-[10px] font-semibold hover:bg-[#1560d4] transition-all duration-200"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Error Modal
  const ErrorModal = () => (
    <>
      {errorMessage && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-[20px] p-6 sm:p-8 max-w-[400px] w-full shadow-2xl animate-scale-in">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <p className="text-2xl font-bold text-red-500">!</p>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                Error
              </h2>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <button
                onClick={() => setErrorMessage("")}
                className="w-full bg-red-500 text-white py-2 sm:py-3 rounded-[10px] font-semibold hover:bg-red-600 transition-all duration-200"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="font-lato">
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
      <SuccessModal />
      <ErrorModal />
      <Navbar />
      {/* Top Page heading banner */}
      <div className="bg-[#F7F7F7B2] py-4 sm:py-6 flex justify-center px-4">
        <div className="flex flex-col sm:flex-row justify-center sm:justify-start gap-2 sm:gap-4 items-center max-w-[1100px]">
          <img
            src={images.Logo}
            alt="logo"
            className="w-[50px] h-[50px] sm:w-[80px] sm:h-[80px] md:w-[95px] md:h-[95px] flex-shrink-0"
          />
          <div className="max-w-[500px]">
            <p className="font-bold text-[16px] sm:text-[20px] md:text-[22px] text-center sm:text-left">
              Book Your Appointment
            </p>
            <p className="font-openSans text-[12px] sm:text-[14px] md:text-[16px] leading-[1.2] text-center sm:text-left">
              Book your appointment with our expert doctors quickly and easily.
              We provide convenient scheduling, minimal waiting time, and
              personalized medical care for every patient.
            </p>
          </div>
        </div>
      </div>
      {/* Bullets */}
      <div className="flex flex-wrap justify-start sm:justify-center items-center gap-3 sm:gap-8 my-6 sm:my-4 px-6">
        <div className="flex gap-2 items-center">
          <div className="p-2 bg-[#E3A1A166] rounded-full flex-shrink-0">
            <CircleCheck color="#000" className="w-5 h-5" />
          </div>
          <p className="font-semibold text-[14px] sm:text-[16px] md:text-[17px]">
            Same-day appointments available
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="p-2 bg-[#C9DCDE] rounded-full flex-shrink-0">
            <CircleCheck color="#000" className="w-5 h-5" />
          </div>
          <p className="font-semibold text-[14px] sm:text-[16px] md:text-[17px]">
            24×7 emergency booking
          </p>
        </div>
      </div>
      {/* Form */}
      <div className="my-10 px-4">
        <div
          className="w-full sm:w-[95%] lg:w-[90%] mx-auto min-h-[600px] sm:min-h-[700px] lg:min-h-[600px] rounded-[20px] bg-cover bg-center flex items-center justify-center md:justify-end p-6 sm:p-10 lg:p-12"
          style={{ backgroundImage: `url(${images.AppointmentImage})` }}
        >
          <div className="w-full sm:w-[90%] md:w-[85%] lg:w-[500px] py-6 sm:py-8 px-6 sm:px-8 bg-white rounded-[20px] shadow-[14px_9px_15.3px_5px_#0000001A]">
            <h1 className="font-bold text-[20px] sm:text-[25px] uppercase text-black text-center leading-[1.2]">
              Book Your Appointment
            </h1>
            <div className="flex flex-col gap-2 mt-3">
              <label
                htmlFor="fullName"
                className="text-[#1F2933] text-sm sm:text-base"
              >
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className={`w-full rounded-[20px] bg-[#F8F8F8] py-2 sm:py-3 px-4 sm:px-5 text-[#9C9C9C] text-sm sm:text-base transition-all duration-200 hover:bg-[#F0F0F0] focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-1 focus:bg-white active:bg-[#E8E8E8] ${
                  errors.fullName ? "ring-2 ring-red-500 bg-red-50" : ""
                }`}
                placeholder="Enter your full name"
              />
              {errors.fullName && (
                <p className="text-red-500 text-xs sm:text-sm font-medium">
                  {errors.fullName}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-3">
              <label
                htmlFor="phone"
                className="text-[#1F2933] text-sm sm:text-base"
              >
                Phone Number
              </label>
              <input
                type="text"
                id="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full rounded-[20px] bg-[#F8F8F8] py-2 sm:py-3 px-4 sm:px-5 text-[#9C9C9C] text-sm sm:text-base transition-all duration-200 hover:bg-[#F0F0F0] focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-1 focus:bg-white active:bg-[#E8E8E8] ${
                  errors.phone ? "ring-2 ring-red-500 bg-red-50" : ""
                }`}
                placeholder="Enter your Phone Number"
              />
              {errors.phone && (
                <p className="text-red-500 text-xs sm:text-sm font-medium">
                  {errors.phone}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-3">
              <label
                htmlFor="email"
                className="text-[#1F2933] text-sm sm:text-base"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full rounded-[20px] bg-[#F8F8F8] py-2 sm:py-3 px-4 sm:px-5 text-[#9C9C9C] text-sm sm:text-base transition-all duration-200 hover:bg-[#F0F0F0] focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-1 focus:bg-white active:bg-[#E8E8E8] ${
                  errors.email ? "ring-2 ring-red-500 bg-red-50" : ""
                }`}
                placeholder="your.email@gmail.com"
              />
              {errors.email && (
                <p className="text-red-500 text-xs sm:text-sm font-medium">
                  {errors.email}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-3">
              <label
                htmlFor="facility"
                className="text-[#1F2933] text-sm sm:text-base"
              >
                Facility
              </label>
              <div className="relative group">
                <select
                  name="facility"
                  id="facility"
                  value={formData.facility}
                  onChange={handleInputChange}
                  className={`w-full rounded-[20px] bg-[#F8F8F8] py-2 sm:py-3 px-4 sm:px-5 pr-10 sm:pr-12 text-black text-sm sm:text-base appearance-none cursor-pointer transition-all duration-200 hover:bg-[#F0F0F0] focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-1 focus:bg-white active:bg-[#E8E8E8] ${
                    errors.facility ? "ring-2 ring-red-500 bg-red-50" : ""
                  }`}
                >
                  <option value="Select Facility">Select Facility</option>
                  <option value="Easy Online Booking">
                    Easy Online Booking
                  </option>
                  <option value="Instant Confirmation">
                    Instant Confirmation
                  </option>
                  <option value="Flexible Time Slots">
                    Flexible Time Slots
                  </option>
                  <option value="No Waiting Time">No Waiting Time</option>
                </select>
                <ChevronDown className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-black pointer-events-none group-hover:text-[#1975FF] transition-colors duration-200" />
              </div>
              {errors.facility && (
                <p className="text-red-500 text-xs sm:text-sm font-medium">
                  {errors.facility}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-3">
              <label
                htmlFor="doctor"
                className="text-[#1F2933] text-sm sm:text-base"
              >
                Doctor
              </label>
              <div className="relative group">
                <select
                  name="doctor"
                  id="doctor"
                  value={formData.doctor}
                  onChange={handleInputChange}
                  disabled={doctorsLoading}
                  className={`w-full rounded-[20px] bg-[#F8F8F8] py-2 sm:py-3 px-4 sm:px-5 pr-10 sm:pr-12 text-black text-sm sm:text-base appearance-none cursor-pointer transition-all duration-200 hover:bg-[#F0F0F0] focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-1 focus:bg-white active:bg-[#E8E8E8] disabled:opacity-50 disabled:cursor-not-allowed ${
                    errors.doctor ? "ring-2 ring-red-500 bg-red-50" : ""
                  }`}
                >
                  <option value="">
                    {doctorsLoading ? "Loading doctors..." : "Choose doctor"}
                  </option>
                  {Array.isArray(doctors) && doctors.map((doctor) => (
                    <option key={doctor._id} value={doctor._id}>
                      {doctor.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-black pointer-events-none group-hover:text-[#1975FF] transition-colors duration-200" />
              </div>
              {errors.doctor && (
                <p className="text-red-500 text-xs sm:text-sm font-medium">
                  {errors.doctor}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col gap-2 mt-3">
                <label
                  htmlFor="date"
                  className="text-[#1F2933] text-sm sm:text-base"
                >
                  Preferred Date
                </label>
                <div className="relative group">
                  <input
                    type="date"
                    id="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className={`w-full rounded-[20px] bg-[#F8F8F8] py-2 sm:py-3 px-4 sm:px-5 pr-10 sm:pr-12 text-black text-sm sm:text-base appearance-none transition-all duration-200 hover:bg-[#F0F0F0] focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-1 focus:bg-white active:bg-[#E8E8E8] ${
                      errors.date ? "ring-2 ring-red-500 bg-red-50" : ""
                    }`}
                  />
                  <CalendarDays className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-black pointer-events-none group-hover:text-[#1975FF] transition-colors duration-200" />
                </div>
                {errors.date && (
                  <p className="text-red-500 text-xs sm:text-sm font-medium">
                    {errors.date}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2 mt-3">
                <label
                  htmlFor="time"
                  className="text-[#1F2933] text-sm sm:text-base"
                >
                  Preferred Time
                </label>
                <div className="relative group">
                  <select
                    name="time"
                    id="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    className={`w-full rounded-[20px] bg-[#F8F8F8] py-2 sm:py-3 px-4 sm:px-5 pr-10 sm:pr-12 text-black text-sm sm:text-base appearance-none cursor-pointer transition-all duration-200 hover:bg-[#F0F0F0] focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-1 focus:bg-white active:bg-[#E8E8E8] ${
                      errors.time ? "ring-2 ring-red-500 bg-red-50" : ""
                    }`}
                  >
                    <option value="">Choose time slot</option>
                    <option value="10:00 A.M">10:00 A.M</option>
                    <option value="12:00 P.M">12:00 P.M.</option>
                    <option value="2:00 P.M">2:00 P.M.</option>
                    <option value="4:00 P.M">4:00 P.M.</option>
                  </select>
                  <ChevronDown className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-black pointer-events-none group-hover:text-[#1975FF] transition-colors duration-200" />
                </div>
                {errors.time && (
                  <p className="text-red-500 text-xs sm:text-sm font-medium">
                    {errors.time}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-3">
              <label
                htmlFor="message"
                className="text-[#1F2933] text-sm sm:text-base"
              >
                Message / Health Concern
              </label>
              <textarea
                id="message"
                rows={3}
                value={formData.message}
                onChange={handleInputChange}
                className={`w-full rounded-[20px] bg-[#F8F8F8] py-2 sm:py-3 px-4 sm:px-5 text-[#9C9C9C] text-sm sm:text-base resize-none transition-all duration-200 hover:bg-[#F0F0F0] focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-1 focus:bg-white active:bg-[#E8E8E8] ${
                  errors.message ? "ring-2 ring-red-500 bg-red-50" : ""
                }`}
                placeholder="Briefly describe your health concern or reason for visit."
              />
              {errors.message && (
                <p className="text-red-500 text-xs sm:text-sm font-medium">
                  {errors.message}
                </p>
              )}
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={`w-fit mx-auto mt-5 bg-[#1975FF] rounded-[8px] sm:rounded-[10px] py-2 sm:py-2.5 pr-2 pl-3 sm:pl-4 flex items-center justify-center gap-2 text-[13px] sm:text-[14px] font-semibold text-white transition-all duration-200 shadow-md group focus:outline-none focus:ring-2 focus:ring-[#1975FF] focus:ring-offset-2 ${
                submitting
                  ? "opacity-70 cursor-not-allowed bg-[#1975FF]"
                  : "hover:bg-[#1560d4] hover:shadow-lg hover:-translate-y-0.5 active:bg-[#1050b8] active:scale-95 active:shadow-md"
              }`}
            >
              {submitting ? "Booking..." : "Book Appointment"}{" "}
              <span
                className={`p-2 bg-white rounded-full transition-transform duration-300 ${!submitting && "group-hover:scale-110"}`}
              >
                <MoveRight
                  color="#000"
                  className="rotate-[-38deg] w-3.5 h-3.5 sm:w-4 sm:h-4"
                />
              </span>
            </button>
          </div>
        </div>
      </div>
      {/*  Emergency CTA  */}
      <div className="py-10 md:py-12 bg-[#F7F7F7B2] px-4 mb-10">
        <h1 className="max-w-[550px] text-center text-[22px] md:text-[32px] font-semibold mx-auto leading-[1.2]">
          Need Immediate Medical Attention?
        </h1>
        <p className="max-w-[480px] font-openSans text-[15px] md:text-[18px] mx-auto text-center mt-3">
          For urgent medical needs, contact our emergency team available 24×7
          for quick and reliable care.
        </p>
        <div className="flex justify-center mt-8">
          <a
            href="tel:+917300906444"
            className="bg-[#E00F1D] rounded-[10px] py-1 pr-1 pl-4 flex items-center gap-2 text-[14px] font-semibold text-white hover:bg-[#b80d18] active:bg-[#900b13] active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg group"
          >
            <Phone color="#ffffff" className="w-4 h-4" />
            +91 7300906444
            <span className="p-2 bg-white rounded-full transition-transform duration-300 group-hover:scale-110">
              <MoveRight color="#000" className="rotate-[-38deg] w-4 h-4" />
            </span>
          </a>
        </div>
      </div>
      {/* Care Process */}
      <h1 className="text-[22px] sm:text-[26px] md:text-[28px] font-semibold text-center mt-6 md:mt-8 leading-[1.2] px-4">
        How It Works
      </h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-[1090px] mx-auto my-6 px-4">
        <div className="p-5 sm:p-6 rounded-[15px] sm:rounded-[20px] text-center group hover:border-transparent hover:shadow-[0px_10px_35px_0px_#CECDDA66] hover:-translate-y-2 hover:bg-[#F8FAFF] active:scale-[0.98] active:shadow-md transition-all duration-500 border border-transparent cursor-pointer">
          <div className="bg-[#E3A1A166] p-3 md:p-4 rounded-full inline-block group-hover:bg-white group-hover:shadow-md transition-all duration-500">
            <FileText
              color="#000"
              className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 group-hover:text-red-500 transition-colors duration-500"
            />
          </div>
          <p className="font-semibold text-[15px] sm:text-[18px] md:text-[20px] my-2 sm:my-2.5 leading-[1.2]">
            Fill the Appointment Form
          </p>
          <p className="max-w-[200px] mx-auto text-[12px] sm:text-[14px] md:text-[15px] lg:text-[16px] leading-[1.4] font-openSans">
            Provide your details and select your preferred time
          </p>
        </div>
        <div className="p-5 sm:p-6 rounded-[15px] sm:rounded-[20px] text-center group hover:border-transparent hover:shadow-[0px_10px_35px_0px_#CECDDA66] hover:-translate-y-2 hover:bg-[#F8FAFF] active:scale-[0.98] active:shadow-md transition-all duration-500 border border-transparent cursor-pointer">
          <div className="bg-[#E4A77F66] p-3 md:p-4 rounded-full inline-block group-hover:bg-white group-hover:shadow-md transition-all duration-500">
            <UserCheck
              color="#000"
              className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 group-hover:text-blue-500 transition-colors duration-500"
            />
          </div>
          <p className="font-semibold text-[15px] sm:text-[18px] md:text-[20px] my-2 sm:my-2.5 leading-[1.2]">
            Choose Doctor & Time
          </p>
          <p className="max-w-[190px] mx-auto text-[12px] sm:text-[14px] md:text-[15px] lg:text-[16px] leading-[1.4] font-openSans">
            Select from our experienced specialists
          </p>
        </div>
        <div className="p-5 sm:p-6 rounded-[15px] sm:rounded-[20px] text-center group hover:border-transparent hover:shadow-[0px_10px_35px_0px_#CECDDA66] hover:-translate-y-2 hover:bg-[#F8FAFF] active:scale-[0.98] active:shadow-md transition-all duration-500 border border-transparent cursor-pointer">
          <div className="bg-[#B8957A66] p-3 md:p-4 rounded-full inline-block group-hover:bg-white group-hover:shadow-md transition-all duration-500">
            <Phone
              color="#000"
              className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 group-hover:text-yellow-500 transition-colors duration-500"
            />
          </div>
          <p className="font-semibold text-[15px] sm:text-[18px] md:text-[20px] my-2 sm:my-2.5 leading-[1.2]">
            Get Confirmation <br /> Call
          </p>
          <p className="max-w-[190px] mx-auto text-[12px] sm:text-[14px] md:text-[15px] lg:text-[16px] leading-[1.4] font-openSans">
            Our team will confirm your appointment
          </p>
        </div>
        <div className="p-5 sm:p-6 rounded-[15px] sm:rounded-[20px] text-center group hover:border-transparent hover:shadow-[0px_10px_35px_0px_#CECDDA66] hover:-translate-y-2 hover:bg-[#F8FAFF] active:scale-[0.98] active:shadow-md transition-all duration-500 border border-transparent cursor-pointer">
          <div className="bg-[#7FAFB366] p-3 md:p-4 rounded-full inline-block group-hover:bg-white group-hover:shadow-md transition-all duration-500">
            <Calendar
              color="#000"
              className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 group-hover:text-green-500 transition-colors duration-500"
            />
          </div>
          <p className="font-semibold text-[15px] sm:text-[18px] md:text-[20px] my-2 sm:my-2.5 leading-[1.2]">
            Visit Hospital for Consultation
          </p>
          <p className="max-w-[190px] mx-auto text-[12px] sm:text-[14px] md:text-[15px] lg:text-[16px] leading-[1.4] font-openSans">
            Meet with your doctor at the scheduled time
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Appointment;
